import { DropdownPaginatedV2 } from "components/commons";
import React, { useCallback, useContext, useState } from "react";
import locale from "localization";
import { useApi } from "hooks";
import { getShifts } from "apis/shift-records.api";
import { StationContext } from "contexts";
import { dateTimeFormatV2 } from "utils";
import { useImperativeHandle } from "react";
import { forwardRef } from "react";

const SelectShiftsPaginated = forwardRef(
  (
    {
      placeholder = locale.searchOrSelect,
      disableAllOption,
      onChange,
      shiftStatus,
      isHideBankDeposit,
      isFlattenedRecord,
      ...props
    },
    ref,
  ) => {
    const { station } = useContext(StationContext);
    const [totalCount, setTotalCount] = useState(9999);
    const [shifts, setShifts] = useState([]);

    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const last14Days = new Date(today);
    last14Days.setDate(today.getDate() - 29);
    last14Days.setHours(0, 0, 0, 0);

    const shiftParams = {
      statuses: [...shiftStatus].toLocaleString(),
      ...(isHideBankDeposit && { isHideBankDeposit: true }),
      ...(isFlattenedRecord && { isFlattenedRecord: true }),
      startDate: last14Days.toISOString(),
      endDate: today.toISOString(),
      order: "desc",
    };
    const {
      request,
      loading,
      result = { shifts: [] },
      ...apiObj
    } = useApi({
      api: getShifts,
      pageError: true,
      params: shiftParams,
    });

    const fetch = useCallback(
      async (params) => {
        const response = await request({
          stationCode: station.stationCode,
          perPage: 10,
          page: params.page || 1,
          ...params,
        });
        // If returned shifts is less than perPage, we've reached the end
        if (response?.shifts && response?.shifts?.length < 10) {
          setTotalCount(((params.page || 1) - 1) * 10 + response.shifts.length);
        }
        // Store all shifts data
        if (response?.shifts) {
          setShifts((prevShifts) => {
            const allShifts = [...prevShifts];
            response.shifts.forEach((newShift) => {
              if (!allShifts.find((s) => s.shiftId === newShift.shiftId)) {
                allShifts.push(newShift);
              }
            });
            return allShifts;
          });
        }
        return response;
      },
      [request, station.stationCode],
    );

    useImperativeHandle(ref, () => ({
      fetch: () => {
        fetch();
      },
    }));

    return (
      <DropdownPaginatedV2
        ref={ref}
        {...props}
        request={async (params) => {
          return await fetch(params);
        }}
        apiError={apiObj?.error}
        total={totalCount}
        loading={loading}
        onChange={(name, { value, label }) => {
          const selectedShift = shifts.find((s) => s?.shiftId === value);
          onChange?.(name, { value, label, name, shift: selectedShift });
        }}
        formatRequest={(result) => {
          const { shifts = [] } = result || {};
          if (shifts.length > 0) {
            const options = shifts.map((d) => {
              return {
                label: `${dateTimeFormatV2(d.startShift)} - ${dateTimeFormatV2(d.endShift)}`,
                value: d.shiftId,
              };
            });
            return options;
          }
          return [];
        }}
        allLabel={props.value?.length === result?.shifts?.length ? locale.all : locale.cashier}
        placeholder={apiObj?.error ? "Failed to load options" : placeholder || "Select"}
        {...(!disableAllOption
          ? {
              allOption: [
                {
                  label: "All",
                  value: "all",
                },
              ],
            }
          : [])}
      />
    );
  },
);

export default SelectShiftsPaginated;
